import { Map } from 'immutable';

import { getPublishStatus, isVelaStatus } from '../listings/statuses';
import { setProductErrors } from './errors';
import { getSize } from '../iterable/getSize';
import { get } from '../iterable/get';

import { STATUS_COLUMN, STATUS_COLUMN_ORDER, STATUS_COLUMNS } from '../../constants/edit';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { PUBLISH_ACTION, STATUS } from '../../constants/listings';
import { NEW, OPERATIONS } from '../../constants/product';
import { SYNC_INDICATOR } from '../../constants/shops';
import { EMPTY, VELA } from '../../constants';
import { MODALS } from '../../constants/modal';
import { DATA } from '../../data';

import actions from '../../actions';

export function shouldAddStatusOperation({ product, status }) {
  const channel = product.get('channel');
  const oldStatus = product.get('status');

  switch (status) {
    case STATUS[VELA].COPIED:
    case STATUS[VELA].STAGING:
    case STATUS[VELA].IMPORTED:
    case STATUS[VELA].UNPUBLISHED: {
      return status === oldStatus;
    }

    case STATUS[VELA].SCHEDULED:
    case STATUS[VELA].TEMPLATE: {
      return false;
    }

    default: {
      switch (channel) {
        case EBAY: {
          switch (status) {
            case STATUS[EBAY].ACTIVE: {
              return !!oldStatus && status !== oldStatus;
            }

            default: {
              return false;
            }
          }
        }

        case ETSY: {
          switch (status) {
            case STATUS[ETSY].ACTIVE: {
              return !!oldStatus && status !== oldStatus;
            }

            case STATUS[ETSY].DRAFT: {
              return isVelaStatus({ channel, status: oldStatus });
            }

            default: {
              return false;
            }
          }
        }

        case FAIRE: {
          switch (status) {
            case STATUS[FAIRE].PUBLISHED:
            case STATUS[FAIRE].DRAFT: {
              return !!oldStatus && status !== oldStatus;
            }

            default: {
              return false;
            }
          }
        }

        case SHOPIFY: {
          switch (status) {
            case STATUS[SHOPIFY].ACTIVE:
            case STATUS[SHOPIFY].DRAFT: {
              return !!oldStatus && status !== oldStatus;
            }

            default: {
              return false;
            }
          }
        }

        default: {
          return false;
        }
      }
    }
  }
}

export function getStatusCoumns(product, stagingStatusEnabled, isVelaOnlyShop = false) {
  const channel = product.get('channel');
  const isComplete = product.get('isComplete');
  const isNew = product.get('productId') === NEW;
  const status = product.get('status');
  let enabled = EMPTY.SET;
  let all = EMPTY.SET;

  function filterVelaOnlyStatus(statusSet) {
    if (isVelaOnlyShop) {
      return statusSet.filter(column =>
        column !== STATUS_COLUMN.PUBLISHED &&
        column !== STATUS_COLUMN.DRAFT &&
        column !== STATUS_COLUMN.ACTIVE
      );
    }
    return statusSet;
  }

  switch (channel) {
    case EBAY: {
      switch (status) {
        case STATUS[EBAY].ACTIVE: {
          all = STATUS_COLUMNS.ACTIVE;
          enabled = all;
          break;
        }

        case STATUS[VELA].COPIED: {
          all = STATUS_COLUMNS.ACTIVE_COPIED;

          if (isComplete) {
            enabled = all;
          }

          return { all, enabled };
        }

        case STATUS[VELA].IMPORTED:
        case STATUS[VELA].UNPUBLISHED: {
          all = STATUS_COLUMNS.ACTIVE_IMPORTED;

          if (isComplete) {
            enabled = all;
          }

          break;
        }

        case STATUS[VELA].SCHEDULED: {
          all = STATUS_COLUMNS.ACTIVE;
          enabled = all;
          break;
        }

        case STATUS[VELA].STAGING: {
          all = STATUS_COLUMNS.ACTIVE_STAGING;
          enabled = all;
          break;
        }

        default: {
          if (isNew) {
            if (stagingStatusEnabled) {
              all = STATUS_COLUMNS.ACTIVE_STAGING;
            } else {
              all = STATUS_COLUMNS.ACTIVE;
            }

            enabled = all;
          }

          break;
        }
      }

      break;
    }

    case ETSY: {
      switch (status) {
        case STATUS[ETSY].ACTIVE: {
          all = STATUS_COLUMNS.ACTIVE_DRAFT;
          break;
        }

        case STATUS[ETSY].DRAFT: {
          all = STATUS_COLUMNS.ACTIVE_DRAFT;
          enabled = all;
          break;
        }

        case STATUS[ETSY].INACTIVE: {
          all = STATUS_COLUMNS.ACTIVE_INACTIVE;
          enabled = all;
          break;
        }

        case STATUS[VELA].COPIED: {
          all = STATUS_COLUMNS.ACTIVE_COPIED_DRAFT;

          if (isComplete) {
            enabled = all;
          }

          break;
        }

        case STATUS[VELA].IMPORTED:
        case STATUS[VELA].UNPUBLISHED: {
          all = STATUS_COLUMNS.ACTIVE_DRAFT_IMPORTED;

          if (isComplete) {
            enabled = all;
          }

          break;
        }

        case STATUS[VELA].SCHEDULED: {
          all = STATUS_COLUMNS.ACTIVE_DRAFT;
          enabled = all;
          break;
        }

        case STATUS[VELA].STAGING: {
          all = STATUS_COLUMNS.ACTIVE_DRAFT_STAGING;
          enabled = all;
          break;
        }

        default: {
          if (isNew) {
            if (stagingStatusEnabled) {
              all = STATUS_COLUMNS.ACTIVE_DRAFT_STAGING;
            } else {
              all = STATUS_COLUMNS.ACTIVE_DRAFT;
            }

            enabled = all;
          }

          break;
        }
      }

      break;
    }

    case FAIRE: {
      switch (status) {
        case STATUS[FAIRE].PUBLISHED: {
          all = STATUS_COLUMNS.PUBLISHED_DRAFT;
          enabled = all;
          break;
        }

        case STATUS[FAIRE].DRAFT: {
          all = STATUS_COLUMNS.PUBLISHED_DRAFT;
          enabled = all;
          break;
        }

        case STATUS[FAIRE].UNPUBLISHED: {
          all = STATUS_COLUMNS.PUBLISHED_DRAFT_UNPUBLISHED;
          enabled = all;
          break;
        }

        case STATUS[VELA].COPIED: {
          all = STATUS_COLUMNS.PUBLISHED_DRAFT_COPIED;

          if (isComplete) {
            enabled = all;
          }

          break;
        }

        case STATUS[VELA].IMPORTED:
        case STATUS[VELA].UNPUBLISHED: {
          all = STATUS_COLUMNS.PUBLISHED_DRAFT_IMPORTED;

          if (isComplete) {
            enabled = all;
          }

          break;
        }

        case STATUS[VELA].STAGING: {
          all = STATUS_COLUMNS.PUBLISHED_DRAFT_STAGING;
          enabled = all;
          break;
        }

        default: {
          if (isNew) {
            if (stagingStatusEnabled) {
              all = STATUS_COLUMNS.PUBLISHED_DRAFT_STAGING;
            } else {
              all = STATUS_COLUMNS.PUBLISHED_DRAFT;
            }

            enabled = all;
          }

          break;
        }
      }

      break;
    }

    case SHOPIFY: {
      switch (status) {
        case STATUS[SHOPIFY].ACTIVE: {
          all = STATUS_COLUMNS.ACTIVE_DRAFT;
          enabled = all;
          break;
        }

        case STATUS[SHOPIFY].ARCHIVED: {
          all = STATUS_COLUMNS.ACTIVE_ARCHIVED_DRAFT;
          enabled = all;
          break;
        }

        case STATUS[SHOPIFY].DRAFT: {
          all = STATUS_COLUMNS.ACTIVE_DRAFT;
          enabled = all;
          break;
        }

        case STATUS[VELA].COPIED: {
          all = STATUS_COLUMNS.ACTIVE_COPIED_DRAFT;

          if (isComplete) {
            enabled = all;
          }

          break;
        }

        case STATUS[VELA].IMPORTED:
        case STATUS[VELA].UNPUBLISHED: {
          all = STATUS_COLUMNS.ACTIVE_DRAFT_IMPORTED;

          if (isComplete) {
            enabled = all;
          }

          break;
        }

        case STATUS[VELA].SCHEDULED: {
          all = STATUS_COLUMNS.ACTIVE_DRAFT;
          enabled = all;
          break;
        }

        case STATUS[VELA].STAGING: {
          all = STATUS_COLUMNS.ACTIVE_DRAFT_STAGING;
          enabled = all;
          break;
        }

        default: {
          if (isNew) {
            if (stagingStatusEnabled) {
              all = STATUS_COLUMNS.ACTIVE_DRAFT_STAGING;
            } else {
              all = STATUS_COLUMNS.ACTIVE_DRAFT;
            }

            enabled = all;
          }

          break;
        }
      }

      break;
    }

    default: {
      break;
    }
  }

  return { all, enabled: filterVelaOnlyStatus(enabled) };
}

export function sortStatusColumns(x, y) {
  return Math.sign(STATUS_COLUMN_ORDER[x] - STATUS_COLUMN_ORDER[y]);
}

export function canBeScheduled(product) {
  const channel = product.get('channel');
  const status = product.get('status');
  const productId = product.get('productId');

  return (
    productId === NEW ||
    status !== getPublishStatus({ action: PUBLISH_ACTION.PUBLISH, channel })
  );
}

export function getProductToSync({ edit, scheduleAt, shopId, shopsData, statuses, userShops }) {
  const product = edit.getIn(['products', shopId]);
  const status = statuses.get(shopId);
  const channel = product.get('channel');
  const oldStatus = product.get('status');
  const productId = product.get('productId');
  const publishStatus = getPublishStatus({ action: PUBLISH_ACTION.PUBLISH, channel });

  function addIndicator(result) {
    function getIndicator() {
      switch (status) {
        case STATUS[VELA].SCHEDULED: {
          return SYNC_INDICATOR.HIDDEN;
        }

        default: {
          if (getSize(result.product.get('operations')) === 0) {
            if (
              scheduleAt !== undefined &&
              result.product.get('status') === status && (
                scheduleAt
                  ? canBeScheduled(result.product)
                  : result.product.get('scheduledAt')
              )
            ) {
              return SYNC_INDICATOR.HIDDEN;
            }

            return undefined;
          }

          return isVelaStatus({ channel, status })
            ? SYNC_INDICATOR.SAVE
            : SYNC_INDICATOR.PUBLISH;
        }
      }
    }

    result.indicator = getIndicator();
    return result;
  }

  if (
    status === STATUS[VELA].SCHEDULED ||
    !isVelaStatus({ channel, status }) ||
    (scheduleAt && canBeScheduled(product.set('status', status))) ||
    (channel === FAIRE && product.get('status') !== status)
  ) {
    if (scheduleAt || status !== STATUS[VELA].STAGING) {
      const shopData = shopsData.get(shopId, EMPTY.MAP);
      const marketplaceId = userShops.getIn(['byId', shopId, 'marketplaceId']);
      const taxonomyData = get([channel, marketplaceId])(DATA);

      const newStatus = statuses.get(shopId);
      const validatedProduct = setProductErrors({
        product: product.set('status', newStatus),
        shopData,
        taxonomyData,
      });

      if (!validatedProduct.get('isComplete')) {
        return {
          edit: edit
            .setIn(['products', shopId], validatedProduct.set('status', oldStatus))
            .set('scrollToErrors', true),
        };
      }
    }
  }

  if (
    status === publishStatus &&
    edit.getIn(['modal', 'type']) !== MODALS.CONFIRMATIONS.PUBLISH &&
    channel === ETSY && (
      productId === NEW ||
      oldStatus !== publishStatus
    )
  ) {
    const modal = Map({
      action: actions.edit.saveAs({ confirmed: true, scheduleAt, statuses }),
      type: MODALS.CONFIRMATIONS.PUBLISH,
    });

    return {
      edit: edit.set('modal', modal),
    };
  }

  function addStatusOperation(operations = EMPTY.LIST) {
    return operations.push(Map({ type: OPERATIONS[channel].STATUS, value: status }));
  }

  if (shouldAddStatusOperation({ product, status })) {
    return addIndicator({ product: product.update('operations', addStatusOperation) });
  } else if (scheduleAt !== undefined || getSize(product.get('operations')) > 0) {
    return addIndicator({ product });
  }

  return { product };
}
